/**
 * Semántica de Google Sheets que las réplicas y los KPIs deben imitar:
 * - Comparación de textos sin distinguir mayúsculas (COUNTIF, SUMIFS, MATCH, "=").
 * - SUM/SUMIFS ignoran textos.
 * - Celda vacía en aritmética = 0.
 */
import type { CellValue } from '../workbook';

export const VALUE_ERROR = '#VALUE!';

export function isBlank(v: CellValue | undefined): boolean {
  return v === null || v === undefined || v === '';
}

export function fold(s: string): string {
  return s.toLocaleLowerCase('es');
}

/** Igualdad tipo criterio de COUNTIF / operador "=" de Sheets. */
export function sheetsEquals(cell: CellValue | undefined, crit: CellValue | undefined): boolean {
  if (isBlank(crit)) return isBlank(cell);
  if (isBlank(cell)) return false;
  if (typeof cell === 'number' && typeof crit === 'number') return cell === crit;
  if (typeof cell === 'number' && typeof crit === 'string' && crit.trim() !== '' && !Number.isNaN(Number(crit))) {
    return cell === Number(crit);
  }
  if (typeof cell === 'string' && typeof crit === 'string') return fold(cell) === fold(crit);
  if (typeof cell === 'boolean' && typeof crit === 'boolean') return cell === crit;
  return String(cell) === String(crit);
}

/** Número para aritmética: vacío → 0; texto numérico → número; otro texto → NaN. */
export function num(v: CellValue | undefined): number {
  if (isBlank(v)) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const n = Number(v);
  return Number.isNaN(n) ? Number.NaN : n;
}

/** Resultado aritmético: NaN se convierte en el error #VALUE! como en la hoja. */
export function arith(n: number): number | string {
  return Number.isFinite(n) ? n : VALUE_ERROR;
}

export function isNumber(v: CellValue | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
